const ID_ATTRIBUTE = 'id';

let _registeredFrame = null;

// Increments per inlined figure so each gets a document-unique id prefix (see isolateSvgIds).
let _figureInstanceCounter = 0;

const inlineFigureStylesheetUrl = new URL('./InlineFigure.css', import.meta.url).href;

function isTextNodeSelectable(node, root) {
    if (!node) {
        return false;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.tagName === "UNSELECTABLE" || node.classList.contains("unselectable")) {
            return false;
        }
    }

    if (node.parentNode && node.parentNode !== root) {
        return isTextNodeSelectable(node.parentNode, root);
    }

    return true;
}

function getNodeIdElement(node) {
    if (node) {
        const isElementNode = (node.nodeType === Node.ELEMENT_NODE);
        if (isElementNode) {
            const hasNodeId = node.getAttribute(ID_ATTRIBUTE);
            if (hasNodeId) {
                return node;
            }
        }
        if (node.parentNode) {
            return getNodeIdElement(node.parentNode);
        }
    }
}

function getTextNodeAtOffset(dmDocument, el, offset) {
    let walker = dmDocument.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    let cumulativeOffset = 0;
    let node = null;

    while (walker.nextNode()) {
        node = walker.currentNode;

        if (!(isTextNodeSelectable(node.parentNode, el))) {
            continue;
        }

        let nodeValueLength = node.nodeValue.length;

        if (cumulativeOffset + nodeValueLength >= offset) {
            return {node: node, offset: offset - cumulativeOffset};
        }

        cumulativeOffset += nodeValueLength;
    }

    return {node: null, offset: 0};
}

function getIndexIntoElementFromRange(dmDocument, el, container, offset) {
    let walker = dmDocument.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    let i = 0;

    while (walker.nextNode()) {
        let node = walker.currentNode;

        if (container.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING) {
            break;
        }

        if (!(isTextNodeSelectable(node.parentNode, el))) {
            continue;
        }

        if (node === container) {
            i += offset;
            break;
        }

        i += node.nodeValue.length;
    }

    return i;
}

function wrapNodeInHighlight(node, tagName, attributes) {
    const highlightElement = node.ownerDocument.createElement(tagName);

    Object.keys(attributes).forEach(function (key) {
        highlightElement.setAttribute(key, attributes[key]);
    });

    const tempRange = node.ownerDocument.createRange();
    tempRange.selectNode(node);
    tempRange.surroundContents(highlightElement);
}

function highlightableNodesInRange(range) {
    // If the start or end node is a text node and only partly in the range, split it.
    if (range.startContainer.nodeType === Node.TEXT_NODE && range.startOffset < range.startContainer.length && range.startOffset > 0) {
        const createdNode = range.startContainer.splitText(range.startOffset);
        // If the end was in the same container, it will now be in the newly created node.
        if (range.endContainer === range.startContainer) {
            range.setEnd(createdNode, range.endOffset - range.startOffset);
        }
        range.setStart(createdNode, 0);
    }

    if (range.endContainer.nodeType === Node.TEXT_NODE && range.endOffset < range.endContainer.length && range.endOffset > 0) {
        range.endContainer.splitText(range.endOffset);
    }

    const walker = range.startContainer.ownerDocument.createTreeWalker(
        range.commonAncestorContainer,
        NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
        function (n) {
            return range.intersectsNode(n) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
        });

    walker.currentNode = range.startContainer;

    const nodes = [];
    do {
        // We only want to highlight text nodes and img nodes.
        if (walker.currentNode.nodeType === Node.TEXT_NODE) {
            // Only push the text node if it contains non-whitespace characters
            if (walker.currentNode.nodeValue.trim() !== "") {
                nodes.push(walker.currentNode);
            }
        } else if (walker.currentNode.nodeType === Node.ELEMENT_NODE && walker.currentNode.nodeName === "IMG") {
            // Push image elements as is
            nodes.push(walker.currentNode);
        }
    } while (walker.nextNode() && range.comparePoint(walker.currentNode, 0) !== 1);

    return nodes;
}

function highlightRange(range, tagName, attributes) {
    if (range.collapsed) {
        return;
    }

    const nodes = highlightableNodesInRange(range);
    for (const node of nodes) {
        wrapNodeInHighlight(node, tagName, attributes);
    }
}

async function handleSelection(dotNetRef, dmDocument, range) {

    let startNodeIdElement = getNodeIdElement(range.startContainer);
    let endNodeIdElement = getNodeIdElement(range.endContainer);

    if (startNodeIdElement && endNodeIdElement) {
        let startIndex = getIndexIntoElementFromRange(
            dmDocument,
            startNodeIdElement,
            range.startContainer,
            range.startOffset);

        let endIndex = getIndexIntoElementFromRange(
            dmDocument,
            endNodeIdElement,
            range.endContainer,
            range.endOffset);

        let data = {
            startDataNodeId: startNodeIdElement.getAttribute(ID_ATTRIBUTE),
            startIndex: startIndex,
            endDataNodeId: endNodeIdElement.getAttribute(ID_ATTRIBUTE),
            endIndex: endIndex,
            text: range.toString().substring(0, Math.min(range.toString().length, 400)) + "..."
        };

        await dotNetRef.invokeMethodAsync('DataModuleSelectionChanged', data);
    }
}

export const initModule = function (frameReference, dotNetRef) {
    if (frameReference) {
        let event = new CustomEvent("initModule", {detail: dotNetRef});
        frameReference.contentDocument.dispatchEvent(event);
    }
};

export const unloadModule = function (frameReference, dotNetRef) {
    unregisterDmFrameEventHandlers();
    if (frameReference) {
        if (frameReference.contentDocument) {
            let event = new CustomEvent("unloadModule", {detail: dotNetRef});
            frameReference.contentDocument.dispatchEvent(event);
        }
        frameReference.srcdoc = '';
    }
};

export const printDataModule = function (frameReference) {
    if (frameReference) {
        frameReference.contentWindow.print();
    }
};

export const setFontSize = function (frameReference, fontSize) {
    if (frameReference) {
        const dmDocument = frameReference.contentDocument || frameReference.contentWindow.document;
        let root = dmDocument.documentElement;
        root.style.fontSize = fontSize + 'px';
    }
};

export const scrollToNode = function (frameReference, startDataNodeId) {
    if (frameReference) {
        const dmDocument = frameReference.contentDocument || frameReference.contentWindow.document;
        const el = dmDocument.getElementById(startDataNodeId);
        if (el) {
            el.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    }
};

export const addAnnotation = function (frameReference, startDataNodeId, startIndex, endDataNodeId, endIndex, annotationId) {
    if (frameReference) {
        const dmDocument = frameReference.contentDocument || frameReference.contentWindow.document;

        const existingAnnotations = dmDocument.querySelectorAll(`[data-annotation-id="${CSS.escape(annotationId)}"]`);
        if (existingAnnotations && existingAnnotations.length > 0) return;

        const startElement = dmDocument.getElementById(startDataNodeId);
        const endElement = dmDocument.getElementById(endDataNodeId);

        if (!startElement || !endElement) {
            console.error('Invalid start or end element');
            return null;
        }

        const startPos = getTextNodeAtOffset(dmDocument, startElement, startIndex);
        const endPos = getTextNodeAtOffset(dmDocument, endElement, endIndex);

        if (!startPos.node || !endPos.node) {
            console.error('Unable to find the correct text node');
            return null;
        }

        let range = dmDocument.createRange();
        range.setStart(startPos.node, startPos.offset);
        range.setEnd(endPos.node, endPos.offset);

        highlightRange(range, 'mark', {
            'data-annotation-id': annotationId
        });
    }
};

export const addAnnotations = function (frameReference, annotations) {
    if (!frameReference || !annotations) return;
    for (const a of annotations) {
        addAnnotation(frameReference, a.startDataNodeId, a.startIndex,
            a.endDataNodeId, a.endIndex, a.annotationId);
    }
};

export const removeAnnotation = function (frameReference, annotationId) {
    if (frameReference) {
        const dmDocument = frameReference.contentDocument || frameReference.contentWindow.document;
        const highlightedNodes = dmDocument.querySelectorAll(`[data-annotation-id="${CSS.escape(annotationId)}"]`);
        if (highlightedNodes) {
            for (const node of highlightedNodes) {
                const parent = node.parentNode;
                while (node.firstChild) {
                    parent.insertBefore(node.firstChild, node);
                }
                parent.removeChild(node);
            }
        }
    }
};

export const registerDmFrameEventHandlers = function (frameReference, dotNetRef) {
    unregisterDmFrameEventHandlers();

    const dmDocument = frameReference.contentDocument || frameReference.contentWindow.document;
    const handler = async () => {
        let selection = dmDocument.getSelection();
        if (!selection.rangeCount) {
            await dotNetRef.invokeMethodAsync('DataModuleSelectionChanged', null);
            return;
        }
        let range = selection.getRangeAt(0);
        await handleSelection(dotNetRef, dmDocument, range);
    };
    _registeredFrame = {doc: dmDocument, handler};
    dmDocument.addEventListener('mouseup', handler);
};

export const unregisterDmFrameEventHandlers = function () {
    if (_registeredFrame) {
        try {
            _registeredFrame.doc.removeEventListener('mouseup', _registeredFrame.handler);
        } catch { /* document may already be detached */
        }
        _registeredFrame = null;
    }
};

// Resolves once the figure image has finished loading (or failed), so its src is the resolvable
// blob/served URL rather than the placeholder it starts with. A timeout keeps a figure that never
// loads from blocking the rest.
function waitForImage(img) {
    if (img.complete && img.naturalWidth > 0) {
        return Promise.resolve();
    }
    return new Promise(resolve => {
        const done = () => {
            img.removeEventListener('load', done);
            img.removeEventListener('error', done);
            clearTimeout(timer);
            resolve();
        };
        const timer = setTimeout(done, 5000);
        img.addEventListener('load', done);
        img.addEventListener('error', done);
    });
}

// Rewrites every id in a figure's SVG to a document-unique prefix and repoints the figure's own
// references at the new ids. Pre-converted figures reuse generic ids (clipRect0, clipRect1, marker
// names, ...) across every sheet; the sidebar viewer never notices because each figure sits in its
// own shadow root, but the inline figures all share the one iframe document. There a url(#id)
// reference resolves to the FIRST matching id in the document, so a later figure is clipped by an
// earlier figure's clip rect and whatever falls outside it is blanked. Prefixing makes each inlined
// figure self-contained, restoring the per-figure isolation the shadow root gives the sidebar.
function isolateSvgIds(svg, prefix) {
    // Map each id - including the root <svg>'s own - to its prefixed form and rewrite it in the same
    // pass; the map keeps the original ids as keys so the reference fix-up below can still resolve
    // them. querySelectorAll returns a static list (and excludes the root), so reassigning during
    // iteration is safe.
    const idMap = new Map();
    const idElements = [...svg.querySelectorAll('[id]')];
    if (svg.id) {
        idElements.push(svg);
    }
    idElements.forEach(element => {
        const newId = prefix + element.id;
        idMap.set(element.id, newId);
        element.id = newId;
    });
    if (idMap.size === 0) {
        return;
    }

    // url(#id) is case-insensitive in CSS/SVG, so match it that way.
    const remapUrls = value => value.replace(
        /url\(\s*['"]?#([^)'"\s]+)['"]?\s*\)/gi,
        (whole, id) => idMap.has(id) ? `url(#${idMap.get(id)})` : whole);

    [svg, ...svg.querySelectorAll('*')].forEach(element => {
        Array.from(element.attributes).forEach(attr => {
            // A local fragment reference (e.g. <use href="#x">, a gradient's href) - covers both
            // href and xlink:href via localName, matching how sanitizeSvg scrubs them.
            if (attr.localName === 'href' && attr.value.startsWith('#') && idMap.has(attr.value.slice(1))) {
                const value = `#${idMap.get(attr.value.slice(1))}`;
                if (attr.namespaceURI) {
                    element.setAttributeNS(attr.namespaceURI, attr.name, value);
                } else {
                    element.setAttribute(attr.name, value);
                }
                return;
            }
            // Any paint/clip/mask/marker/filter reference carried on an attribute.
            if (/url\(/i.test(attr.value)) {
                const remapped = remapUrls(attr.value);
                if (remapped !== attr.value) {
                    element.setAttribute(attr.name, remapped);
                }
            }
        });
    });
}

function ensureInlineFigureStyles(doc) {
    if (doc.querySelector('link[data-up-inline-figure-styles]')) {
        return;
    }
    const stylesheet = doc.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = inlineFigureStylesheetUrl;
    stylesheet.dataset.upInlineFigureStyles = '';
    (doc.head || doc.documentElement).appendChild(stylesheet);
}

// Renders the figure's SVG inline (so its shapes become real DOM nodes the theme recolour can tag) in
// place of the <img>, while keeping the original <img> in the DOM (hidden) so the figure's existing
// click behaviour - opening it in the figure viewer, wired to the <img> by the stylesheet - still
// fires: clicks on the inline SVG are forwarded to it. Returns the inlined <svg>, or null if the
// figure is not an SVG or could not be fetched (the <img> is then left untouched).
async function inlineSvgFigure(doc, img, sanitizeSvg) {
    await waitForImage(img);

    const source = img.currentSrc || img.src;
    if (!source) {
        return null;
    }

    const response = await fetch(source);
    if (!response.ok) {
        return null;
    }
    const text = await response.text();
    if (!text.includes('<svg')) {
        return null;
    }

    const parsed = new DOMParser().parseFromString(text, 'image/svg+xml').querySelector('svg');
    if (!parsed) {
        return null;
    }

    const svg = sanitizeSvg(doc.importNode(parsed, true));
    isolateSvgIds(svg, `fig${++_figureInstanceCounter}-`);
    if (img.className) {
        svg.setAttribute('class', img.className);
    }
    svg.classList.add('up-inline-svg-figure');
    if (img.dataset.icn) {
        svg.dataset.icn = img.dataset.icn;
    }
    // Match the image's own responsive width so the inline SVG scales exactly as the <img> did: read its
    // rendered width as a percentage of its container rather than imposing a fixed 50%, which is wrong
    // for figure types whose container differs (the multi-sheet figures) and was cropping them. Measured
    // before the <img> is hidden below; a figure that isn't laid out (e.g. a hidden sheet) has a zero
    // box, so it is left as the original <img>. The viewBox supplies the aspect ratio for height:auto;
    // the figure's absolute cm/mm width/height are dropped so they don't fight the percentage width.
    const box = img.getBoundingClientRect();
    const containerWidth = img.parentElement ? img.parentElement.getBoundingClientRect().width : 0;
    if (box.width <= 0 || containerWidth <= 0) {
        return null;
    }
    const viewBoxParts = (svg.getAttribute('viewBox') || '').split(/[\s,]+/);
    if (viewBoxParts.length === 4 && parseFloat(viewBoxParts[3]) > 0) {
        svg.removeAttribute('height');
    }
    svg.setAttribute('width', `${(box.width / containerWidth) * 100}%`);
    svg.setAttribute('cursor', 'pointer');
    svg.addEventListener('click', () => img.click());

    // Keep the <img> for its click wiring, but hidden and marked so a re-run skips it.
    img.classList.add('up-inline-svg-source');
    img.dataset.figureRecoloured = 'true';
    img.insertAdjacentElement('beforebegin', svg);
    return svg;
}

// Recolours the inline document figures to follow the page theme, mirroring the sidebar figure viewer.
// SVG figures (rendered as <img>) are inlined and their black/white shapes retargeted to the document's
// own text/background colours, so they track whatever drives the document theme. Already-inline figures
// are recoloured in place; source images are fetched once and their paints refresh on theme changes. Any
// per-figure failure is logged and skipped without disturbing the rest of the document. The caller
// invokes this only when the per-tenant "theme-coloured figures" setting is on; with it off, inline
// figures are simply left as their native image.
export const recolourInlineFigures = async function (frameReference) {
    if (!frameReference) {
        return;
    }
    const doc = frameReference.contentDocument || frameReference.contentWindow.document;
    if (!doc || !doc.body) {
        return;
    }

    // Sibling RCL path: a nested import() resolves relative to this module's own URL
    // (_content/UP.Viewer.Pages/), so reach across to _content/UP.Components/ with "../".
    const {recolourSvg, sanitizeSvg} =
        await import('../UP.Components/FigureTheme.min.js');
    ensureInlineFigureStyles(doc);

    // The framed document themes off the OS color-scheme, not the app's light/dark toggle, so the
    // figures are driven from the app theme instead, copied in as concrete colours and refreshed
    // whenever this runs (including on toggle). The line colour matches the document text (--body).
    // The background uses the document's own content surface (--background) - the surface the text and
    // figures sit on - NOT the sidebar's darker panel surface (--background-darken). That keeps every
    // inline figure blending into the page identically, whether or not its source SVG bakes in an
    // opaque background element: one that does is painted this same surface, one that doesn't lets it
    // show through, so both look the same instead of differing by how the figure was converted.
    const appStyle = getComputedStyle(document.body);
    const line = appStyle.getPropertyValue('--body').trim();
    const background = appStyle.getPropertyValue('--background').trim();
    const colours = {
        line: line ? `hsl(${line})` : 'canvastext',
        background: background ? `hsl(${background})` : 'canvas',
    };

    // Any loadable figure carries data-icn (that is how the document resolves its src). Thumbnails for
    // video/3D assets are excluded, and the <svg> content check inside inlineSvgFigure filters out
    // raster figures, so only SVG figures are inlined.
    const figureSelector = '[data-icn]:not(.thumbnail):not(.multimedia-thumbnail)';

    const inlineFigures = doc.querySelectorAll(`img${figureSelector}:not([data-figure-recoloured])`);
    await Promise.all(Array.from(inlineFigures).map(async img => {
        try {
            const svg = await inlineSvgFigure(doc, img, sanitizeSvg);
            if (svg) {
                svg.dataset.figureRecoloured = 'true';
            }
        } catch (e) {
            console.warn('Failed to inline figure for recolouring', e);
        }
    }));

    // Sanitise native inline figures too, then refresh all figure paints. Already-processed figures
    // retain their original colour classification in FigureTheme's WeakMap, so a theme toggle only
    // updates the concrete presentation-attribute values.
    doc.querySelectorAll(`svg${figureSelector}`).forEach(svg => {
        sanitizeSvg(svg);
        recolourSvg(svg, colours);
        svg.dataset.figureRecoloured = 'true';
    });
};

export const parseWarningsAndCautions = function (frameReference) {
    if (frameReference) {
        const dmDocument = frameReference.contentDocument || frameReference.contentWindow.document;
        const warnings = dmDocument.querySelectorAll('.ietp-warning');
        const cautions = dmDocument.querySelectorAll('.ietp-caution');

        const safeTags = new Set(['p', 'br', 'b', 'i', 'em', 'strong', 'u', 'sub', 'sup', 'span', 'div', 'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'dl', 'dt', 'dd', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

        const sanitizeHtml = (htmlContent) => {
            const tempDiv = dmDocument.createElement('div');
            tempDiv.innerHTML = htmlContent;

            const sanitizeNode = (node) => {
                const children = Array.from(node.childNodes);
                children.forEach(child => {
                    if (child.nodeType === Node.ELEMENT_NODE) {
                        const tagName = child.tagName.toLowerCase();
                        if (!safeTags.has(tagName)) {
                            const textNode = dmDocument.createTextNode(child.textContent);
                            child.parentNode.replaceChild(textNode, child);
                        } else {
                            const attrs = Array.from(child.attributes);
                            attrs.forEach(attr => {
                                if (attr.name.startsWith('on') || (attr.name === 'href' && attr.value.trimStart().startsWith('javascript:'))) {
                                    child.removeAttribute(attr.name);
                                }
                            });
                            sanitizeNode(child);
                        }
                    }
                });
            };

            sanitizeNode(tempDiv);
            return tempDiv.innerHTML;
        };

        return {
            warnings: Array.from(warnings).map(el => sanitizeHtml(el.innerHTML)),
            cautions: Array.from(cautions).map(el => sanitizeHtml(el.innerHTML))
        };
    }
};
